import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserPlus, Check, Ban, Clock, Loader2, ShieldCheck, KeyRound, Link2, Copy, Trash2, AlertTriangle, RotateCcw, Pencil } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const STATUS_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  suspended: "bg-rose-50 text-rose-700 border-rose-200",
};

const ROLE_STYLE = {
  super_admin: "bg-slate-900 text-white border-slate-700",
  admin: "bg-cyan-50 text-cyan-700 border-cyan-200",
  sales: "bg-sky-50 text-sky-700 border-sky-200",
  estimator: "bg-violet-50 text-violet-700 border-violet-200",
  installer: "bg-amber-50 text-amber-800 border-amber-200",
  reception: "bg-pink-50 text-pink-700 border-pink-200",
};

const RESULT_STYLE = {
  success: "text-emerald-600",
  bad_credentials: "text-rose-600",
  locked: "text-rose-700",
  disabled: "text-slate-500",
  blocked_pending: "text-amber-600",
  blocked_suspended: "text-rose-600",
};

const ROLES = ["sales", "estimator", "installer", "reception", "admin"];

function PermissionGrid({ catalog, value, onChange, disabled }) {
  const groups = [...new Set((catalog?.pages || []).map((p) => p.group))];
  return (
    <div className="space-y-3 max-h-64 overflow-y-auto border border-slate-200 rounded-sm p-3" data-testid="permission-grid">
      {disabled && <p className="text-xs text-slate-500">Admins always see everything.</p>}
      {groups.map((g) => (
        <div key={g}>
          <div className="stamp-label text-slate-500 mb-1.5">{g}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {(catalog?.pages || []).filter((p) => p.group === g).map((p) => (
              <label key={p.key} className={`flex items-center gap-2 text-sm ${disabled ? "opacity-50" : "cursor-pointer"}`}>
                <Checkbox
                  checked={disabled || value.includes(p.key)}
                  disabled={disabled}
                  onCheckedChange={(v) => onChange(v ? [...value, p.key] : value.filter((k) => k !== p.key))}
                  data-testid={`perm-${p.key}`}
                />
                <span className="text-slate-700">{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RolePermFields({ catalog, role, setRole, perms, setPerms }) {
  return (
    <>
      <div>
        <Label className="stamp-label text-slate-600">Role</Label>
        <Select value={role} onValueChange={(r) => { setRole(r); setPerms(catalog?.presets?.[r] || []); }}>
          <SelectTrigger className="mt-1 rounded-sm h-10 capitalize" data-testid="role-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="stamp-label text-slate-600">Page access — tick exactly what they can see</Label>
        <div className="mt-1">
          <PermissionGrid catalog={catalog} value={perms} onChange={setPerms} />
        </div>
      </div>
    </>
  );
}

export default function TeamAccess() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", title: "" });
  const [addRole, setAddRole] = useState("sales");
  const [addPerms, setAddPerms] = useState(null); // null = use preset
  const [setupLink, setSetupLink] = useState(null); // { url, name }
  const [delUser, setDelUser] = useState(null);
  // Iter-75: single unified Edit dialog (tabs: Profile / Access / Password).
  // Replaces the old three separate dialogs (Edit profile / Access / Reset).
  const [editUser, setEditUser] = useState(null);
  const [editTab, setEditTab] = useState("profile");
  const [editRole, setEditRole] = useState("sales");
  const [editPerms, setEditPerms] = useState([]);
  const [editFlags, setEditFlags] = useState({ quote: false, po: false, supplier: false });
  const [profileDraft, setProfileDraft] = useState({ name: "", title: "" });
  const [confirmReset, setConfirmReset] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["team-users"],
    queryFn: async () => (await api.get("/auth/users")).data,
  });
  const { data: catalog } = useQuery({
    queryKey: ["perm-catalog"],
    queryFn: async () => (await api.get("/auth/permissions/catalog")).data,
  });
  const { data: events = [] } = useQuery({
    queryKey: ["login-events"],
    queryFn: async () => (await api.get("/auth/login-events")).data,
    refetchInterval: 30000,
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/auth/users/${id}/status`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team-users"] }); toast.success("Access updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const deleteUser = useMutation({
    mutationFn: async (id) => (await api.delete(`/auth/users/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-users"] });
      toast.success("Account removed from the allowed list");
      setDelUser(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const addUser = useMutation({
    mutationFn: async () => (await api.post("/auth/invite", {
      ...form,
      role: addRole,
      permissions: addPerms ?? catalog?.presets?.[addRole] ?? [],
    })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["team-users"] });
      setAddOpen(false);
      const nm = form.name;
      setForm({ name: "", email: "", title: "" });
      setAddRole("sales");
      setAddPerms(null);
      setSetupLink({ url: data.setup_url, name: nm });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add user"),
  });

  const reissueLink = useMutation({
    mutationFn: async (u) => ({ data: (await api.post(`/auth/users/${u.id}/setup-link`)).data, name: u.name }),
    onSuccess: ({ data, name }) => setSetupLink({ url: data.setup_url, name }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create link"),
  });

  const resetPassword = useMutation({
    mutationFn: async (u) => ({ data: (await api.post(`/auth/users/${u.id}/reset-password`)).data, name: u.name }),
    onSuccess: ({ data, name }) => {
      qc.invalidateQueries({ queryKey: ["team-users"] });
      setConfirmReset(false);
      closeEdit();
      setSetupLink({ url: data.setup_url, name, resetMode: true });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reset password"),
  });

  const copyLink = () => {
    try { navigator.clipboard.writeText(setupLink?.url || ""); toast.success("Setup link copied"); }
    catch (e) { console.warn("clipboard copy failed", e); toast.error("Copy failed — select and copy the link manually"); }
  };

  const saveAccess = useMutation({
    mutationFn: async () => (await api.patch(`/auth/users/${editUser.id}/access`, {
      role: editRole,
      permissions: editPerms,
      quote_approver: editFlags.quote,
      po_approver: editFlags.po,
      supplier_approver: editFlags.supplier,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-users"] });
      toast.success("Access updated — applies on their next page load");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  // Iter-75: single dialog opener — Tab controls which section starts visible.
  const openEdit = (u, tab = "profile") => {
    setEditUser(u);
    setEditTab(tab);
    setEditRole(u.role || "sales");
    setEditPerms(u.permissions?.length ? u.permissions : (catalog?.presets?.[u.role] || []));
    setEditFlags({ quote: !!u.quote_approver, po: !!u.po_approver, supplier: !!u.supplier_approver });
    setProfileDraft({ name: u.name || "", title: u.title || "" });
    setConfirmReset(false);
  };

  const closeEdit = () => { setEditUser(null); setConfirmReset(false); };

  const saveProfile = useMutation({
    mutationFn: async () => (await api.patch(`/auth/users/${editUser.id}/profile`, profileDraft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-users"] });
      toast.success("Profile updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  return (
    <div>
      <PageHeader
        title="Staff & Roles"
        subtitle="Manage staff members, their roles and page access. Only these emails can sign in — everyone else is denied. People create their own password on first sign-in."
        breadcrumb={["System", "Staff & Roles"]}
        actions={
          <Button data-testid="add-user-btn" onClick={() => { setAddPerms(null); setAddOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <UserPlus size={16} className="mr-2" /> Add account
          </Button>
        }
      />

      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 space-y-6">
        {/* Users */}
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="users-card">
          <div className="stamp-label text-cyan-600 px-4 pt-4 pb-2 flex items-center gap-1.5"><ShieldCheck size={13} /> Accounts</div>
          {isLoading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr className="text-left stamp-label text-slate-500">
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => {
                    const status = u.status || "approved";
                    return (
                      <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.email}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-slate-900">{u.name}</div>
                          {u.title && <div className="text-[10px] text-slate-500">{u.title}</div>}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className={`rounded-sm capitalize ${ROLE_STYLE[u.role] || ""}`}>{u.role}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className={`rounded-sm capitalize ${STATUS_STYLE[status] || ""}`}>{status}</Badge>
                          {!u.password_set && (
                            <Badge variant="outline" className="rounded-sm border-sky-300 bg-sky-50 text-sky-700 ml-1" data-testid={`awaiting-setup-${u.email}`}>awaiting password</Badge>
                          )}
                          {u.password_set && status === "pending" && (
                            <Badge variant="outline" className="rounded-sm border-amber-300 bg-amber-50 text-amber-800 ml-1" data-testid={`awaiting-approval-${u.email}`}>needs your approval</Badge>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right space-x-1.5 whitespace-nowrap">
                          {/* Iter-75: single Edit button replaces the trio of
                              Edit profile / Access / Reset password. Opens
                              the unified dialog with tabs for each area. */}
                          <Button size="sm" variant="outline" data-testid={`edit-user-${u.email}`}
                            onClick={() => openEdit(u, "profile")}
                            className="rounded-sm h-8 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                            title="Edit profile, access & password">
                            <Pencil size={13} className="mr-1" /> Edit
                          </Button>
                          {!u.password_set && (
                            <Button size="sm" variant="outline" data-testid={`setup-link-${u.email}`}
                              onClick={() => reissueLink.mutate(u)} disabled={reissueLink.isPending}
                              className="rounded-sm h-8 border-sky-300 text-sky-700 hover:bg-sky-50">
                              <Link2 size={13} className="mr-1" /> Setup link
                            </Button>
                          )}
                          {status !== "approved" && (
                            <Button size="sm" variant="outline" data-testid={`approve-${u.email}`}
                              onClick={() => setStatus.mutate({ id: u.id, status: "approved" })}
                              className="rounded-sm h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                              <Check size={13} className="mr-1" /> Approve
                            </Button>
                          )}
                          {status === "pending" && u.password_set && u.role !== "super_admin" && (
                            <Button size="sm" variant="outline" data-testid={`decline-${u.email}`}
                              onClick={() => setDelUser(u)}
                              className="rounded-sm h-8 border-rose-300 text-rose-700 hover:bg-rose-50">
                              <Ban size={13} className="mr-1" /> Decline
                            </Button>
                          )}
                          {status !== "suspended" && status !== "pending" && u.role !== "super_admin" && (
                            <Button size="sm" variant="outline" data-testid={`suspend-${u.email}`}
                              onClick={() => setStatus.mutate({ id: u.id, status: "suspended" })}
                              className="rounded-sm h-8 border-rose-300 text-rose-700 hover:bg-rose-50">
                              <Ban size={13} className="mr-1" /> Suspend
                            </Button>
                          )}
                          {u.role !== "super_admin" && (
                            <Button size="sm" variant="ghost" data-testid={`delete-user-${u.email}`}
                              onClick={() => setDelUser(u)}
                              title="Delete account"
                              className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-rose-600">
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Login activity */}
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="login-events-card">
          <div className="stamp-label text-cyan-600 px-4 pt-4 pb-2 flex items-center gap-1.5"><Clock size={13} /> Login activity (latest 100)</div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200 sticky top-0">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Result</th>
                  <th className="px-4 py-2">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No login attempts recorded yet.</td></tr>
                )}
                {events.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2 text-slate-500 font-mono text-xs whitespace-nowrap">{fmtDate(e.ts)}</td>
                    <td className="px-4 py-2 text-slate-800">{e.email}</td>
                    <td className={`px-4 py-2 font-semibold ${RESULT_STYLE[e.result] || "text-slate-600"}`}>{e.result}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-xs">{e.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add account dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Add a user to the allowed list</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Only emails on this list can access the app — everyone else gets <b>Access Denied</b>. The person creates their <b>own password</b> the first time they sign in.</p>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="stamp-label text-slate-600">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm h-10" data-testid="new-user-name" placeholder="Jane Installer" />
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Email</Label>
                <Input type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} className="mt-1 rounded-sm h-10" data-testid="new-user-email" placeholder="jane@ccfi.com.au" />
              </div>
            </div>
            <div>
              <Label className="stamp-label text-slate-600">Job title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm h-10" data-testid="new-user-title" placeholder="e.g. Manager, Installer, Company Owner" />
            </div>
            <RolePermFields
              catalog={catalog}
              role={addRole}
              setRole={setAddRole}
              perms={addPerms ?? catalog?.presets?.[addRole] ?? []}
              setPerms={setAddPerms}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => addUser.mutate()} disabled={addUser.isPending || !form.name || !form.email}
              data-testid="create-user-btn" className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {addUser.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />} Add to allowed list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-75: Unified Edit dialog — Profile / Access / Password tabs.
          Replaces three previously-separate dialogs so the admin has ONE
          entry point per user. */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Pencil size={18} className="text-cyan-600" /> Edit {editUser?.name || editUser?.email}
              {editUser?.role === "super_admin" && (
                <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500 font-normal">super admin</span>
              )}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-mono">{editUser?.email}</span>
            </p>
          </DialogHeader>

          <Tabs value={editTab} onValueChange={setEditTab} className="mt-2">
            <TabsList className="grid grid-cols-3 rounded-sm w-full">
              <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-sm">Profile</TabsTrigger>
              <TabsTrigger value="access" data-testid="tab-access" className="rounded-sm">Access</TabsTrigger>
              <TabsTrigger value="password" data-testid="tab-password" className="rounded-sm">Password</TabsTrigger>
            </TabsList>

            {/* Profile tab */}
            <TabsContent value="profile" className="pt-4 space-y-3">
              <div>
                <Label className="stamp-label text-slate-600">Email (login identity — cannot change)</Label>
                <Input value={editUser?.email || ""} disabled
                  className="mt-1 rounded-sm h-10 bg-slate-50 text-slate-500 font-mono text-xs"
                  data-testid="profile-email-readonly" />
                <p className="text-[10px] text-slate-400 mt-1">
                  To change email: delete this account and invite the person again with the new address (their history stays on file).
                </p>
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Full name</Label>
                <Input value={profileDraft.name}
                  onChange={(e) => setProfileDraft({ ...profileDraft, name: e.target.value })}
                  className="mt-1 rounded-sm h-10"
                  placeholder="Trent Mason"
                  data-testid="profile-name-input" />
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Job title</Label>
                <Input value={profileDraft.title}
                  onChange={(e) => setProfileDraft({ ...profileDraft, title: e.target.value })}
                  className="mt-1 rounded-sm h-10"
                  placeholder="e.g. Manager, Installer, Company Owner"
                  data-testid="profile-title-input" />
              </div>
              <div className="flex justify-end pt-2">
                <Button data-testid="save-profile-btn" onClick={() => saveProfile.mutate()}
                  disabled={saveProfile.isPending || !profileDraft.name?.trim()}
                  className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
                  {saveProfile.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />} Save profile
                </Button>
              </div>
            </TabsContent>

            {/* Access tab */}
            <TabsContent value="access" className="pt-4 space-y-3">
              {editUser?.role === "super_admin" ? (
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                  <div className="font-semibold flex items-center gap-1.5"><ShieldCheck size={12} /> Super admin — full access</div>
                  <p className="mt-1">The super admin has every permission by default and cannot be role-restricted from here.</p>
                </div>
              ) : (
                <>
                  <RolePermFields catalog={catalog} role={editRole} setRole={setEditRole} perms={editPerms} setPerms={setEditPerms} />
                  <label className="flex items-start gap-2 border border-slate-200 rounded-sm p-3 cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={editFlags.quote} onCheckedChange={(v) => setEditFlags((f) => ({ ...f, quote: !!v }))} data-testid="quote-approver-checkbox" className="mt-0.5" />
                    <span>
                      <span className="text-sm font-medium text-slate-800">Quote approver</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Can approve quotes submitted for review — Complete &amp; Send and PDF download only unlock after approval</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 border border-cyan-200 bg-cyan-50/50 rounded-sm p-3 cursor-pointer hover:bg-cyan-50">
                    <Checkbox checked={editFlags.po} onCheckedChange={(v) => setEditFlags((f) => ({ ...f, po: !!v }))} data-testid="po-approver-checkbox" className="mt-0.5" />
                    <span>
                      <span className="text-sm font-medium text-slate-800">PO approver</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Can approve purchase orders before they're sent to suppliers</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 border border-violet-200 bg-violet-50/50 rounded-sm p-3 cursor-pointer hover:bg-violet-50">
                    <Checkbox checked={editFlags.supplier} onCheckedChange={(v) => setEditFlags((f) => ({ ...f, supplier: !!v }))} data-testid="supplier-approver-checkbox" className="mt-0.5" />
                    <span>
                      <span className="text-sm font-medium text-slate-800">Supplier approver</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Reviews supplier onboarding submissions, sees bank details and gets document expiry alerts</span>
                    </span>
                  </label>
                  <div className="flex justify-end pt-2">
                    <Button onClick={() => saveAccess.mutate()} disabled={saveAccess.isPending}
                      data-testid="save-access-btn" className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
                      {saveAccess.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />} Save access
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Password tab */}
            <TabsContent value="password" className="pt-4 space-y-3">
              {editUser?.role === "super_admin" ? (
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                  The super admin password can only be changed by signing in as super admin — not from here.
                </div>
              ) : !editUser?.password_set ? (
                <div className="rounded-sm border border-sky-200 bg-sky-50 p-4 text-xs text-sky-800 space-y-2">
                  <div className="font-semibold flex items-center gap-1.5"><Link2 size={12} /> Account never activated</div>
                  <p>
                    This user hasn't set a password yet. Use the <b>Setup link</b> button on the row to reissue their one-time link.
                  </p>
                </div>
              ) : !confirmReset ? (
                <div className="space-y-3">
                  <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 space-y-2">
                    <div className="font-semibold flex items-center gap-1.5">
                      <AlertTriangle size={13} /> Reset password
                    </div>
                    <p>
                      Clicking <b>Reset password</b> will:
                    </p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Clear their current password immediately</li>
                      <li>Sign out any active sessions they have</li>
                      <li>Generate a fresh one-time setup link for you to hand to them</li>
                    </ul>
                    <p className="text-amber-700">
                      Use this if the person has forgotten their password, or their account was compromised.
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <Button data-testid="start-reset-password-btn"
                      onClick={() => setConfirmReset(true)}
                      className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white">
                      <RotateCcw size={14} className="mr-1.5" /> Reset password
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-sm border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 space-y-2">
                    <div className="font-semibold flex items-center gap-1.5"><AlertTriangle size={14} /> Confirm reset</div>
                    <p>
                      This will clear <b>{editUser?.name}</b>&apos;s password and sign them out of all sessions. Are you sure?
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setConfirmReset(false)} className="rounded-sm">Cancel</Button>
                    <Button data-testid="confirm-reset-password-btn"
                      onClick={() => resetPassword.mutate(editUser)}
                      disabled={resetPassword.isPending}
                      className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
                      {resetPassword.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RotateCcw size={14} className="mr-1.5" />}
                      Confirm reset & get new link
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
            <Button variant="ghost" onClick={closeEdit} className="rounded-sm" data-testid="edit-user-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <Dialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-500" /> Remove {delUser?.name}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p><b>{delUser?.email}</b> will be removed from the allowed list and can no longer sign in. Their past bookings, tasks and audit history are kept.</p>
            <p className="text-xs text-slate-400">Prefer a temporary block? Use <b>Suspend</b> instead — it keeps the account for later.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelUser(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-delete-user-btn" onClick={() => deleteUser.mutate(delUser.id)} disabled={deleteUser.isPending}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {deleteUser.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup link dialog — hand this to the person to activate their account */}
      <Dialog open={!!setupLink} onOpenChange={(o) => !o && setSetupLink(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="setup-link-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Link2 size={18} className="text-sky-600" /> {setupLink?.resetMode ? "New password setup link" : "Setup link"} for {setupLink?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {setupLink?.resetMode
              ? <>Password for <b>{setupLink?.name}</b> has been cleared. Send this one-time link to them (text, WhatsApp, or in person) so they can set a new password.</>
              : <>Send this one-time link to <b>{setupLink?.name}</b> (text, WhatsApp, or in person). They open it to create their own password. The link works once and no account can be activated without it.</>
            }
          </p>
          <div className="flex gap-2">
            <div className="flex-1 border border-slate-200 bg-slate-50 rounded-sm p-2.5 font-mono text-[11px] text-slate-700 break-all" data-testid="setup-link-url">{setupLink?.url}</div>
            <Button size="sm" onClick={copyLink} className="rounded-sm shrink-0 self-center bg-sky-600 hover:bg-sky-700" data-testid="copy-setup-link-btn"><Copy size={14} className="mr-1.5" /> Copy</Button>
          </div>
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2.5">Treat this like a password — anyone with the link can set this account&apos;s password. Re-issue it from the table if it&apos;s lost.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupLink(null)} className="rounded-sm">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
